import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, radius, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  card: { ...s.card, padding: '24px' },
  input: { ...s.input, padding: '10px', fontSize: '14px' },
  label: { ...s.labelNormal, fontSize: '14px' },
  select: { ...s.select, padding: '10px' },
}

const CANDIDATE_ORDER_OPTIONS = [
  { value: 'random', label: 'Rastgele' },
  { value: 'newest', label: 'Son Eklenenler' },
  { value: 'price_asc', label: 'En Düşük Fiyat' },
  { value: 'price_desc', label: 'En Yüksek Fiyat' },
]

const TEMPO_OPTIONS = [
  { value: 'short', label: 'Kısa ve hızlı → 100-200 sayfalık kitapları tercih et' },
  { value: 'long', label: 'Uzun ve derinlikli → 400+ sayfalık kitapları tercih et' },
  { value: 'explore', label: 'Yeni şeyler keşfetmek istiyorum → Ana kategorinin dışından da kitaplar ekle' },
]

function getAllAnswers(questions) {
  return questions.flatMap(q => (q.answers || []).map(a => ({ ...a, questionText: q.question_text })))
}

function rulesToThinker(rules, categories) {
  if (!rules || !rules.length) return ''
  const lines = ['Kullanıcının quiz cevaplarına göre en uygun kitapları seç. Aşağıdaki kuralları kesinlikle uygula:']
  const comboRules = rules.filter(r => r.type === 'combo')
  const singleRules = rules.filter(r => r.type === 'single')
  const tempoRules = rules.filter(r => r.type === 'tempo')
  if (comboRules.length) {
    lines.push('\nOKUMA DENEYİMİ + TÜR KOMBİNASYONLARI:')
    comboRules.forEach(r => {
      const catNames = (r.category_codes || []).map(code => {
        const cat = categories.find(c => c.category_code === code)
        return cat ? cat.category_name : code
      }).join(', ')
      lines.push(`Kullanıcı "${r.answer1}" ve "${r.answer2}" seçtiyse: ${catNames} kategorilerinden seç.`)
    })
  }
  if (singleRules.length) {
    lines.push('\nTEK CEVAP KURALLARI:')
    singleRules.forEach(r => {
      const catNames = (r.category_codes || []).map(code => {
        const cat = categories.find(c => c.category_code === code)
        return cat ? cat.category_name : code
      }).join(', ')
      lines.push(`Kullanıcı "${r.answer1}" seçtiyse: ${catNames} kategorilerinden seç.`)
    })
  }
  if (tempoRules.length) {
    lines.push('\nTEMPO KURALLARI:')
    tempoRules.forEach(r => {
      const opt = TEMPO_OPTIONS.find(o => o.value === r.tempo)
      if (opt) lines.push(opt.label.replace(' → ', ': ').replace('Kısa ve hızlı: ', 'Kullanıcı "Kısa ve hızlı" seçtiyse: ').replace('Uzun ve derinlikli: ', 'Kullanıcı "Uzun ve derinlikli" seçtiyse: ').replace('Yeni şeyler keşfetmek istiyorum: ', 'Kullanıcı "Yeni şeyler keşfetmek istiyorum" seçtiyse: '))
    })
  }
  return lines.join('\n')
}

function Quiz() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [quizzes, setQuizzes] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('list')
  const [selectedQuiz, setSelectedQuiz] = useState(null)
  const [categories, setCategories] = useState([])

  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  useEffect(() => {
    if (!selectedBrand) return
    fetchQuizzes()
    fetchCategories()
  }, [selectedBrand?.id]) // eslint-disable-line

  function fetchQuizzes() {
    setLoading(true)
    axios.get(`${API}/admin/brands/${selectedBrand.id}/quizzes`, { headers })
      .then(res => { setQuizzes(res.data); setLoading(false) })
      .catch(() => setLoading(false))
  }

  function fetchCategories() {
    axios.get(`${API}/admin/brands/${selectedBrand.id}/categories`, { headers })
      .then(res => setCategories(res.data.flat || []))
      .catch(() => {})
  }

  async function handleCreate() {
    const res = await axios.post(`${API}/admin/brands/${selectedBrand.id}/quizzes`, {
      title: 'Yeni Quiz', description: '', category_code: '', category_name: '', thinker: ''
    }, { headers })
    fetchQuizzes()
    setSelectedQuiz(res.data)
    setView('edit')
  }

  async function handleDelete(id) {
    if (!window.confirm('Bu quiz\'i silmek istediğinize emin misiniz?')) return
    await axios.delete(`${API}/admin/quizzes/${id}`, { headers })
    fetchQuizzes()
  }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <p style={{ ...ui.notice, display: 'inline-block', marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  if (view === 'edit' && selectedQuiz) {
    return <QuizEdit quiz={selectedQuiz} categories={categories} headers={headers}
      onSaved={(updatedQuiz) => {
        if (updatedQuiz) setSelectedQuiz(updatedQuiz)
        fetchQuizzes()
        setView('list')
      }}
      onBack={() => setView('list')}
      onManageQuestions={() => setView('questions')} />
  }

  if (view === 'questions' && selectedQuiz) {
    return <QuizQuestions quizId={selectedQuiz.id} quizTitle={selectedQuiz.title} headers={headers}
      onBack={() => setView('list')} />
  }

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 className="sx-break" style={{ minWidth: 0 }}>Smart Quiz — {selectedBrand.name}</h2>
        <button onClick={handleCreate} style={{ ...s.btnPrimary, padding: '10px 20px' }}>
          + Yeni Quiz
        </button>
      </div>
      {loading ? <p>Yükleniyor...</p> : quizzes.length === 0 ? (
        <div style={{ ...style.card, textAlign: 'center', padding: '48px', color: colors.textMuted, fontSize: '13px' }}>
          Henüz quiz yok. Yeni bir quiz oluşturun.
        </div>
      ) : quizzes.map(quiz => (
        <div key={quiz.id} style={style.card}>
          <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ minWidth: 0, flex: '1 1 220px' }}>
              <p className="sx-break" style={{ fontWeight: 600, fontSize: '16px', marginBottom: '6px' }}>{quiz.title}</p>
              <div className="sx-break" style={{ display: 'flex', gap: '16px', fontSize: '13px', color: colors.textSecondary, flexWrap: 'wrap' }}>
                {quiz.category_name && <span>Kategori: {quiz.category_name}</span>}
                {quiz.description && <span>{quiz.description}</span>}
                <span>Oluşturulma: {new Date(quiz.created_at).toLocaleDateString('tr-TR')}</span>
              </div>
            </div>
            <div className="sx-wrap" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0, marginLeft: '16px', flexWrap: 'wrap', maxWidth: '100%' }}>
              <span style={ui.status(quiz.is_active)}>
                <span style={ui.statusDot(quiz.is_active)} />
                {quiz.is_active ? 'Aktif' : 'Pasif'}
              </span>
              <button onClick={() => { setSelectedQuiz(quiz); setView('questions') }} style={ui.btnSm}>Sorular</button>
              <button onClick={() => { setSelectedQuiz(quiz); setView('edit') }} style={ui.btnSm}>Düzenle</button>
              <button onClick={() => handleDelete(quiz.id)} style={ui.btnDangerQuiet}>Sil</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function QuizEdit({ quiz, categories, headers, onSaved, onBack, onManageQuestions }) {
  const [form, setForm] = useState({ ...quiz, candidate_order: quiz.candidate_order || 'random' })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [rules, setRules] = useState(() => {
    if (quiz.thinker_rules) {
      try {
        return Array.isArray(quiz.thinker_rules) ? quiz.thinker_rules : JSON.parse(quiz.thinker_rules)
      } catch { return [] }
    }
    return []
  })
  const [questions, setQuestions] = useState([])
  const [thinkerMode, setThinkerMode] = useState(quiz.thinker_rules && (Array.isArray(quiz.thinker_rules) ? quiz.thinker_rules.length : 0) > 0 ? 'visual' : 'text')
  const [categoriesReady, setCategoriesReady] = useState(false)

  // categories prop gelince hazır işaretle
  useEffect(() => {
    if (categories && categories.length > 0) {
      setCategoriesReady(true)
    }
  }, [categories])

  useEffect(() => {
    axios.get(`${API}/admin/quizzes/${quiz.id}`, { headers })
      .then(res => setQuestions(res.data.questions || []))
      .catch(() => {})
  }, []) // eslint-disable-line

  const allAnswers = questions.flatMap(q => (q.answers || []).map(a => ({ ...a, questionText: q.question_text })))

  function addRule(type) {
    setRules([...rules, { id: Date.now(), type, answer1: '', answer2: '', category_codes: [], tempo: 'short' }])
  }
  function updateRule(id, field, value) {
    setRules(rules.map(r => r.id === id ? { ...r, [field]: value } : r))
  }
  function removeRule(id) {
    setRules(rules.filter(r => r.id !== id))
  }
  function toggleCategory(ruleId, code) {
    setRules(rules.map(r => {
      if (r.id !== ruleId) return r
      const codes = r.category_codes || []
      return { ...r, category_codes: codes.includes(code) ? codes.filter(c => c !== code) : [...codes, code] }
    }))
  }

  async function handleSave() {
    setSaving(true)
    const thinkerText = thinkerMode === 'visual' ? rulesToThinker(rules, categories) : form.thinker
    const updatedForm = { ...form, thinker: thinkerText, thinker_rules: thinkerMode === 'visual' ? rules : null }
    await axios.put(`${API}/admin/quizzes/${quiz.id}`, updatedForm, { headers })
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2500)
    onSaved(updatedForm)
  }

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={s.btnSecondary}>← Geri</button>
        <h2 style={{ margin: 0, minWidth: 0 }}>Quiz Düzenle</h2>
      </div>
      <div style={style.card}>
        <div style={{ marginBottom: '16px' }}>
          <label style={style.label}>Quiz Başlığı</label>
          <input style={style.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={style.label}>Açıklama</label>
          <input style={style.input} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Kısa açıklama (opsiyonel)" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={style.label}>Kategori <span style={{ fontSize: '12px', color: colors.textMuted, fontWeight: 400 }}>(boş = tüm ürünler)</span></label>
            <select style={style.input} value={form.category_code || ''}
              onChange={e => {
                const cat = categories.find(c => c.category_code === e.target.value)
                setForm({ ...form, category_code: e.target.value, category_name: cat?.category_name || '' })
              }}>
              <option value="">Tüm Ürünler</option>
              {categories.map(c => (
                <option key={c.category_code} value={c.category_code}>
                  {'  '.repeat(Math.max(0, (c.level || 1) - 1))}{c.category_name} ({c.category_code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={style.label}>AI'a Gönderilecek Ürün Sıralaması</label>
            <select style={style.input} value={form.candidate_order || 'random'} onChange={e => setForm({ ...form, candidate_order: e.target.value })}>
              {CANDIDATE_ORDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="sx-wrap" style={{ marginBottom: '16px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_active !== false} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
            Aktif
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer', flexWrap: 'wrap' }}>
            <input type="checkbox" checked={form.include_description || false} onChange={e => setForm({ ...form, include_description: e.target.checked })} />
            Ürün açıklamalarını AI'a dahil et
          </label>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={style.label}>Ürün Öneri Kuralları (Thinker)</label>
          <div className="sx-wrap" style={{ ...ui.segmented, marginBottom: '12px', flexWrap: 'wrap', maxWidth: '100%' }}>
            <button onClick={() => setThinkerMode('visual')}
              style={ui.segmentedBtn(thinkerMode === 'visual')}>
              Görsel Editör
            </button>
            <button onClick={() => {
              const thinkerText = rulesToThinker(rules, categories)
              setForm(f => ({ ...f, thinker: thinkerText || f.thinker }))
              setThinkerMode('text')
            }}
              style={ui.segmentedBtn(thinkerMode === 'text')}>
              Metin Editörü
            </button>
          </div>
          {thinkerMode === 'text' ? (
            <textarea
              style={{ ...style.input, minHeight: '180px', resize: 'vertical', fontFamily: 'monospace', lineHeight: '1.6', fontSize: '13px' }}
              value={form.thinker || ''} onChange={e => setForm({ ...form, thinker: e.target.value })}
              placeholder="Ürün öneri kurallarını buraya yazın..." />
          ) : !categoriesReady ? (
            <p style={ui.emptyText}>Kategoriler yükleniyor...</p>
          ) : (
            <ThinkerRuleEditor
              rules={rules}
              allAnswers={allAnswers}
              categories={categories}
              onAddRule={addRule}
              onUpdateRule={updateRule}
              onRemoveRule={removeRule}
              onToggleCategory={toggleCategory}
            />
          )}
        </div>
        <div className="sx-wrap" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={handleSave} disabled={saving} style={{ ...s.btnPrimary, padding: '10px 24px' }}>
            {saving ? 'Kaydediliyor...' : saved ? '✓ Kaydedildi' : 'Kaydet'}
          </button>
          <button onClick={onManageQuestions} style={{ ...s.btnSecondary, padding: '10px 24px' }}>Soruları Yönet →</button>
          <button onClick={onBack} style={{ ...s.btnSecondary, padding: '10px 24px' }}>İptal</button>
        </div>
      </div>
    </div>
  )
}

function ThinkerRuleEditor({ rules, allAnswers, categories, onAddRule, onUpdateRule, onRemoveRule, onToggleCategory }) {
  const [expandedRule, setExpandedRule] = useState(null)

  return (
    <div>
      <div style={{ ...ui.notice, marginBottom: '12px' }}>
        Her kural: kullanıcının seçtiği cevaplara göre hangi kategorilerden ürün önerileceğini belirler.
      </div>
      {rules.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px', color: colors.textMuted, fontSize: '13px', border: `1px dashed ${colors.border}`, borderRadius: radius.md, marginBottom: '12px' }}>
          Henüz kural yok. Aşağıdan kural ekleyin.
        </div>
      )}
      {rules.map((rule, i) => (
        <div key={rule.id} style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, marginBottom: '8px', overflow: 'hidden' }}>
          <div className="sx-wrap" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: colors.bgPage, cursor: 'pointer', flexWrap: 'wrap' }}
            onClick={() => setExpandedRule(expandedRule === rule.id ? null : rule.id)}>
            <span style={{ fontSize: '12px', color: colors.textSecondary, minWidth: '20px', fontWeight: 600 }}>{i + 1}.</span>
            {rule.type === 'tempo' ? (
              <span className="sx-break" style={{ flex: '1 1 180px', minWidth: 0, fontSize: '13px', color: colors.textPrimary }}>
                {TEMPO_OPTIONS.find(o => o.value === rule.tempo)?.label || 'Tempo kuralı'}
              </span>
            ) : (
              <span className="sx-break" style={{ flex: '1 1 180px', minWidth: 0, fontSize: '13px', color: colors.textPrimary }}>
                {rule.answer1 ? `"${rule.answer1}"` : <span style={{ color: colors.textMuted }}>Cevap seçilmedi</span>}
                {rule.type === 'combo' && (rule.answer2 ? ` + "${rule.answer2}"` : <span style={{ color: colors.textMuted }}> + cevap seçilmedi</span>)}
                {' → '}
                {rule.category_codes?.length > 0
                  ? rule.category_codes.map(c => categories.find(cat => cat.category_code === c)?.category_name || c).join(', ')
                  : <span style={{ color: colors.textMuted }}>Kategori seçilmedi</span>}
              </span>
            )}
            <span style={ui.chip}>
              {rule.type === 'combo' ? 'Kombinasyon' : rule.type === 'tempo' ? 'Tempo' : 'Tek Cevap'}
            </span>
            <button onClick={e => { e.stopPropagation(); onRemoveRule(rule.id) }}
              style={{ ...ui.btnDangerQuiet, padding: '4px 9px' }}>Sil</button>
            <span style={{ color: colors.textMuted, fontSize: '12px' }}>{expandedRule === rule.id ? '▲' : '▼'}</span>
          </div>
          {expandedRule === rule.id && (
            <div style={{ padding: '14px', borderTop: `1px solid ${colors.borderLight}`, background: colors.bgCard }}>
              {rule.type === 'tempo' ? (
                <div>
                  <label style={{ ...style.label, fontSize: '13px' }}>Tempo Kuralı</label>
                  <select style={style.select} value={rule.tempo} onChange={e => onUpdateRule(rule.id, 'tempo', e.target.value)}>
                    {TEMPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: rule.type === 'combo' ? '1fr 1fr' : '1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ ...style.label, fontSize: '13px' }}>{rule.type === 'combo' ? 'Birinci Cevap' : 'Cevap'}</label>
                      <select style={style.select} value={rule.answer1} onChange={e => onUpdateRule(rule.id, 'answer1', e.target.value)}>
                        <option value="">Cevap seçin...</option>
                        {allAnswers.map(a => (
                          <option key={a.id} value={a.answer_text}>{a.questionText?.substring(0, 20)}: {a.answer_text}</option>
                        ))}
                      </select>
                    </div>
                    {rule.type === 'combo' && (
                      <div>
                        <label style={{ ...style.label, fontSize: '13px' }}>İkinci Cevap</label>
                        <select style={style.select} value={rule.answer2} onChange={e => onUpdateRule(rule.id, 'answer2', e.target.value)}>
                          <option value="">Cevap seçin...</option>
                          {allAnswers.map(a => (
                            <option key={a.id} value={a.answer_text}>{a.questionText?.substring(0, 20)}: {a.answer_text}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ ...style.label, fontSize: '13px' }}>Kategoriler <span style={{ color: colors.textMuted, fontWeight: 400 }}>(birden fazla seçilebilir)</span></label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px', maxHeight: '200px', overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '8px' }}>
                      {categories.filter(c => c.level === 1 || !c.level).map(cat => (
                        <label key={cat.category_code} className="sx-break" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', padding: '3px', minWidth: 0 }}>
                          <input type="checkbox"
                            checked={(rule.category_codes || []).includes(cat.category_code)}
                            onChange={() => onToggleCategory(rule.id, cat.category_code)} />
                          {cat.category_name}
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
      <div className="sx-wrap" style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => onAddRule('combo')} style={ui.btnSm}>+ Kombinasyon Kuralı</button>
        <button onClick={() => onAddRule('single')} style={ui.btnSm}>+ Tek Cevap Kuralı</button>
        <button onClick={() => onAddRule('tempo')} style={ui.btnSm}>+ Tempo Kuralı</button>
      </div>
    </div>
  )
}

function QuizQuestions({ quizId, quizTitle, headers, onBack }) {
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingQuestion, setEditingQuestion] = useState(null)
  const [editingAnswer, setEditingAnswer] = useState(null)
  const [newQuestionText, setNewQuestionText] = useState('')
  const [addingQuestion, setAddingQuestion] = useState(false)

  useEffect(() => { fetchQuestions() }, []) // eslint-disable-line

  function fetchQuestions() {
    setLoading(true)
    axios.get(`${API}/admin/quizzes/${quizId}`, { headers })
      .then(res => { setQuestions(res.data.questions || []); setLoading(false) })
      .catch(() => setLoading(false))
  }

  async function addQuestion() {
    if (!newQuestionText.trim()) return
    await axios.post(`${API}/admin/quizzes/${quizId}/questions`, { question_text: newQuestionText, order_index: questions.length }, { headers })
    setNewQuestionText(''); setAddingQuestion(false); fetchQuestions()
  }

  async function deleteQuestion(id) {
    if (!window.confirm('Bu soruyu silmek istediğinize emin misiniz?')) return
    await axios.delete(`${API}/admin/questions/${id}`, { headers })
    fetchQuestions()
  }

  async function saveQuestion(q) {
    await axios.put(`${API}/admin/questions/${q.id}`, q, { headers })
    setEditingQuestion(null); fetchQuestions()
  }

  async function addAnswer(questionId, answerText, tags, branchToQuestionId, visibleFromAnswerIds) {
    await axios.post(`${API}/admin/questions/${questionId}/answers`, {
      answer_text: answerText, tags, order_index: 0,
      branch_to_question_id: branchToQuestionId || null,
      visible_from_answer_ids: visibleFromAnswerIds?.length ? visibleFromAnswerIds : null
    }, { headers })
    fetchQuestions()
  }

  async function saveAnswer(a) {
    await axios.put(`${API}/admin/answers/${a.id}`, a, { headers })
    setEditingAnswer(null); fetchQuestions()
  }

  async function deleteAnswer(id) {
    await axios.delete(`${API}/admin/answers/${id}`, { headers })
    fetchQuestions()
  }

  const allAnswers = getAllAnswers(questions)

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={s.btnSecondary}>← Geri</button>
        <h2 className="sx-break" style={{ margin: 0, minWidth: 0 }}>{quizTitle} — Sorular</h2>
      </div>
      <div style={{ ...ui.notice, padding: '12px 16px', marginBottom: '16px', lineHeight: 1.6 }}>
        <strong>Sonraki Soru:</strong> Bu cevap seçilince hangi soruya geçileceğini belirler. Boş = sıradaki soru.<br />
        <strong>Görünüm Koşulu:</strong> Bu cevap yalnızca seçili önceki cevaplardan biri seçildiyse gösterilir. Boş = her zaman göster.
      </div>
      {loading ? <p>Yükleniyor...</p> : (
        <>
          {questions.map((q, qi) => (
            <div key={q.id} style={style.card}>
              {editingQuestion === q.id ? (
                <QuestionEdit question={q} onSave={saveQuestion} onCancel={() => setEditingQuestion(null)} />
              ) : (
                <>
                  <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                      <span style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', display: 'block' }}>
                        Soru {qi + 1}
                        {q.is_end && <span style={{ ...ui.chip, marginLeft: '6px' }}>Son Soru</span>}
                      </span>
                      <p className="sx-break" style={{ fontWeight: 600, fontSize: '15px', margin: 0 }}>{q.question_text}</p>
                    </div>
                    <div className="sx-wrap" style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '16px', flexWrap: 'wrap' }}>
                      <button onClick={() => setEditingQuestion(q.id)} style={ui.btnSm}>Düzenle</button>
                      <button onClick={() => deleteQuestion(q.id)} style={ui.btnDangerQuiet}>Sil</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {q.answers.map(a => (
                      <div key={a.id} style={{ background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.md, padding: '12px 16px' }}>
                        {editingAnswer === a.id ? (
                          <AnswerEdit answer={a} questions={questions} allAnswers={allAnswers} onSave={saveAnswer} onCancel={() => setEditingAnswer(null)} />
                        ) : (
                          <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                              <span className="sx-break" style={{ fontSize: '14px', fontWeight: 500 }}>{a.answer_text}</span>
                              <div className="sx-break" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {a.branch_to_question_id && (
                                  <span style={{ ...ui.chip, whiteSpace: 'normal' }}>
                                    → {questions.find(qq => qq.id === a.branch_to_question_id)?.question_text?.substring(0, 30) || 'Soru ' + a.branch_to_question_id}
                                  </span>
                                )}
                                {a.visible_from_answer_ids?.length > 0 && (
                                  <span style={{ ...ui.chip, whiteSpace: 'normal' }}>
                                    Koşul: {a.visible_from_answer_ids.map(id => allAnswers.find(x => x.id === id)?.answer_text?.substring(0, 15) || id).join(', ')}
                                  </span>
                                )}
                                {a.tags && a.tags.split(',').map((t, i) => t.trim() && (
                                  <span key={i} style={{ ...ui.chip, whiteSpace: 'normal' }}>{t.trim()}</span>
                                ))}
                              </div>
                            </div>
                            <div className="sx-wrap" style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '12px', flexWrap: 'wrap' }}>
                              <button onClick={() => setEditingAnswer(a.id)} style={ui.btnSm}>Düzenle</button>
                              <button onClick={() => deleteAnswer(a.id)} style={ui.btnDangerQuiet}>Sil</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <AddAnswer questionId={q.id} questions={questions} allAnswers={allAnswers} onAdd={addAnswer} />
                  </div>
                </>
              )}
            </div>
          ))}
          <div style={style.card}>
            {addingQuestion ? (
              <div>
                <label style={style.label}>Yeni Soru</label>
                <textarea style={{ ...style.input, minHeight: '80px', resize: 'vertical', marginBottom: '12px' }}
                  value={newQuestionText} onChange={e => setNewQuestionText(e.target.value)} placeholder="Soru metnini yazın..." />
                <div className="sx-wrap" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button onClick={addQuestion} style={s.btnPrimary}>Ekle</button>
                  <button onClick={() => { setAddingQuestion(false); setNewQuestionText('') }} style={s.btnSecondary}>İptal</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingQuestion(true)}
                style={{ ...s.btn, background: 'transparent', border: `1px dashed ${colors.border}`, width: '100%', justifyContent: 'center', padding: '16px', color: colors.textSecondary, fontSize: '13px' }}>
                + Soru Ekle
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function QuestionEdit({ question, onSave, onCancel }) {
  const [text, setText] = useState(question.question_text)
  const [isEnd, setIsEnd] = useState(question.is_end || false)
  return (
    <div>
      <label style={style.label}>Soru Metni</label>
      <textarea style={{ ...style.input, minHeight: '80px', resize: 'vertical', marginBottom: '12px' }}
        value={text} onChange={e => setText(e.target.value)} />
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '12px', flexWrap: 'wrap' }}>
        <input type="checkbox" checked={isEnd} onChange={e => setIsEnd(e.target.checked)} />
        Son soru (bu sorudan sonra sonuç gösterilir)
      </label>
      <div className="sx-wrap" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button onClick={() => onSave({ ...question, question_text: text, is_end: isEnd })} style={{ ...ui.btnSm, fontWeight: '600' }}>Kaydet</button>
        <button onClick={onCancel} style={ui.btnSm}>İptal</button>
      </div>
    </div>
  )
}

function AnswerEdit({ answer, questions, allAnswers, onSave, onCancel }) {
  const [text, setText] = useState(answer.answer_text)
  const [tags, setTags] = useState(answer.tags || '')
  const [branchTo, setBranchTo] = useState(answer.branch_to_question_id || '')
  const [visibleFromIds, setVisibleFromIds] = useState(answer.visible_from_answer_ids || [])

  function toggleVisibleFrom(id) {
    const numId = parseInt(id)
    setVisibleFromIds(prev => prev.includes(numId) ? prev.filter(x => x !== numId) : [...prev, numId])
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
        <div>
          <label style={{ ...style.label, fontSize: '13px' }}>Cevap Metni</label>
          <input style={style.input} value={text} onChange={e => setText(e.target.value)} />
        </div>
        <div>
          <label style={{ ...style.label, fontSize: '13px' }}>Sonraki Soru <span style={{ color: colors.textMuted, fontWeight: 400 }}>(boş = sıradaki)</span></label>
          <select style={style.input} value={branchTo} onChange={e => setBranchTo(e.target.value)}>
            <option value="">Sıradaki soru</option>
            {questions.map(q => <option key={q.id} value={q.id}>{q.question_text.substring(0, 50)}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label style={{ ...style.label, fontSize: '13px' }}>Görünüm Koşulu <span style={{ color: colors.textMuted, fontWeight: 400 }}>(boş = her zaman göster)</span></label>
        <div style={{ background: colors.bgPage, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '10px', maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {allAnswers.filter(a => a.id !== answer.id).map(a => (
            <label key={a.id} className="sx-break" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', flexWrap: 'wrap', minWidth: 0 }}>
              <input type="checkbox" checked={visibleFromIds.includes(a.id)} onChange={() => toggleVisibleFrom(a.id)} />
              <span style={{ color: colors.textSecondary, fontSize: '11px' }}>{a.questionText?.substring(0, 20)}:</span>
              <span>{a.answer_text.substring(0, 35)}</span>
            </label>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label style={{ ...style.label, fontSize: '13px' }}>Etiketler</label>
        <input style={style.input} value={tags} onChange={e => setTags(e.target.value)} placeholder="örn: kurgu, roman" />
      </div>
      <div className="sx-wrap" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => onSave({ ...answer, answer_text: text, tags, branch_to_question_id: branchTo || null, visible_from_answer_ids: visibleFromIds.length ? visibleFromIds : null })}
          style={{ ...ui.btnSm, fontWeight: '600' }}>Kaydet</button>
        <button onClick={onCancel} style={ui.btnSm}>İptal</button>
      </div>
    </div>
  )
}

function AddAnswer({ questionId, questions, allAnswers, onAdd }) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [tags, setTags] = useState('')
  const [branchTo, setBranchTo] = useState('')
  const [visibleFromIds, setVisibleFromIds] = useState([])

  function toggleVisibleFrom(id) {
    const numId = parseInt(id)
    setVisibleFromIds(prev => prev.includes(numId) ? prev.filter(x => x !== numId) : [...prev, numId])
  }

  function handleAdd() {
    if (!text.trim()) return
    onAdd(questionId, text, tags, branchTo || null, visibleFromIds.length ? visibleFromIds : null)
    setText(''); setTags(''); setBranchTo(''); setVisibleFromIds([]); setOpen(false)
  }

  if (!open) return (
    <button onClick={() => setOpen(true)}
      style={{ ...s.btn, background: 'transparent', border: `1px dashed ${colors.border}`, width: '100%', justifyContent: 'center', padding: '8px', color: colors.textSecondary, fontSize: '13px' }}>
      + Cevap Ekle
    </button>
  )

  return (
    <div style={{ background: colors.bgPage, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '12px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <div>
          <label style={{ ...style.label, fontSize: '12px' }}>Cevap Metni</label>
          <input style={style.input} value={text} onChange={e => setText(e.target.value)} placeholder="Cevap..." />
        </div>
        <div>
          <label style={{ ...style.label, fontSize: '12px' }}>Sonraki Soru</label>
          <select style={style.input} value={branchTo} onChange={e => setBranchTo(e.target.value)}>
            <option value="">Sıradaki soru</option>
            {questions.map(q => <option key={q.id} value={q.id}>{q.question_text.substring(0, 50)}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <label style={{ ...style.label, fontSize: '12px' }}>Görünüm Koşulu</label>
        <div style={{ background: colors.bgCard, borderRadius: radius.sm, padding: '8px', maxHeight: '120px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', border: `1px solid ${colors.border}` }}>
          {allAnswers.map(a => (
            <label key={a.id} className="sx-break" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer', flexWrap: 'wrap', minWidth: 0 }}>
              <input type="checkbox" checked={visibleFromIds.includes(a.id)} onChange={() => toggleVisibleFrom(a.id)} />
              <span style={{ color: colors.textSecondary, fontSize: '11px' }}>{a.questionText?.substring(0, 15)}:</span>
              <span>{a.answer_text.substring(0, 30)}</span>
            </label>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <input style={style.input} value={tags} onChange={e => setTags(e.target.value)} placeholder="Etiketler..." />
      </div>
      <div className="sx-wrap" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={handleAdd} style={{ ...ui.btnSm, fontWeight: '600' }}>Ekle</button>
        <button onClick={() => { setOpen(false); setText(''); setTags(''); setBranchTo(''); setVisibleFromIds([]) }}
          style={ui.btnSm}>İptal</button>
      </div>
    </div>
  )
}

export default Quiz